import type {
  CargonizerCustomer,
  Shipment,
  ShipmentCreateInput,
  ShipmentCreateReturnInput,
  ShipmentCreateViaCargonizerInput,
  ShipmentEndpoint,
  ShipmentEvidence,
  ShipmentList,
  ShipmentPatch,
  TrackingRefreshResult,
} from '../domain/shipment';
import {
  cargonizerCustomerSchema,
  shipmentEndpointSchema,
  shipmentEvidenceSchema,
  shipmentSchema,
  trackingRefreshResultSchema,
} from '../domain/validation';
import {
  ApiError,
  ConflictError,
  DuplicateError,
  ForbiddenError,
  NotFoundError,
  parseShipmentErrorReason,
  ShipmentBookingError,
  ValidationError,
} from './errors';
import { withoutUndefined } from './patch';
import type { ShipmentsApi } from './shipments-api';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';

// See live-inventory-api.ts's redirectToSignIn for why: a 401 from the relay
// means the session is gone, so send the user straight back through sign-in
// rather than surfacing a generic error.
function redirectToSignIn(): void {
  const returnTo = `${window.location.pathname}${window.location.search}`;
  window.location.assign(`/auth/google?returnTo=${encodeURIComponent(returnTo)}`);
}

const GRPC_CODES: Record<number, string> = {
  3: 'VALIDATION', // INVALID_ARGUMENT
  5: 'NOT_FOUND',
  7: 'FORBIDDEN', // PERMISSION_DENIED
  10: 'CONFLICT', // ABORTED
};

/**
 * Talks to the local dev API server (see server/), which relays every /v1/*
 * request to the real leatherback backend. Model and error-mapping mirror
 * live-inventory-api.ts.
 */
export class LiveShipmentsApi implements ShipmentsApi {
  private async fetchJSON(path: string, init?: RequestInit): Promise<Record<string, unknown>> {
    let response: Response;
    try {
      response = await fetch(`${BASE_URL}${path}`, {
        ...init,
        headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
      });
    } catch (error) {
      throw new ApiError(
        error instanceof Error ? error.message : 'Network request failed',
        'NETWORK',
      );
    }
    const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
    if (!response.ok) {
      if (response.status === 401) {
        redirectToSignIn();
        throw new ApiError('Your session expired. Redirecting to sign-in…', 'UNAUTHENTICATED');
      }
      const message =
        typeof body.error === 'string'
          ? body.error
          : typeof body.message === 'string'
            ? body.message
            : `Request failed with status ${response.status}`;
      const currentVersion =
        typeof body.currentVersion === 'number' ? body.currentVersion : undefined;
      // Cargonizer booking errors lead with a stable reason (see
      // shipments.proto); keep it as the error code.
      const reasoned = parseShipmentErrorReason(message);
      if (reasoned) throw new ShipmentBookingError(reasoned.detail, reasoned.reason);
      // grpc-gateway reports canonical numeric gRPC codes.
      const code = GRPC_CODES[body.code as number] ?? body.code;
      switch (code) {
        case 'NOT_FOUND':
          throw new NotFoundError(message);
        case 'CONFLICT':
          throw new ConflictError(message, currentVersion);
        case 'DUPLICATE':
          throw new DuplicateError(message);
        case 'VALIDATION':
          throw new ValidationError(message);
        case 'FORBIDDEN':
          throw new ForbiddenError(message);
        default:
          if (/not authorized/i.test(message)) throw new ForbiddenError(message);
          throw new ApiError(message, 'INTERNAL');
      }
    }
    return body;
  }

  // One malformed row must not hide every other shipment: skip it and say so.
  async listShipments(): Promise<ShipmentList> {
    const params = new URLSearchParams({ pageSize: '5000' });
    const raw = await this.fetchJSON(`/v1/shipments?${params.toString()}`);
    const rawShipments: unknown[] = Array.isArray(raw.shipments) ? raw.shipments : [];
    const list: ShipmentList = { shipments: [], skipped: [] };
    for (const row of rawShipments) {
      const parsed = shipmentSchema.safeParse(row);
      if (parsed.success) {
        list.shipments.push(parsed.data);
        continue;
      }
      const id =
        typeof row === 'object' && row !== null && typeof (row as { id?: unknown }).id === 'string'
          ? (row as { id: string }).id
          : 'unknown';
      const issue = parsed.error.issues[0];
      const problem = issue ? `${issue.path.join('.') || 'shipment'}: ${issue.message}` : 'invalid';
      console.warn(`Skipped shipment ${id}: ${problem}`);
      list.skipped.push({ id, problem });
    }
    return list;
  }

  async getShipment(id: string): Promise<Shipment> {
    const raw = await this.fetchJSON(`/v1/shipments/${encodeURIComponent(id)}`);
    return shipmentSchema.parse(raw);
  }

  async updateShipment(patch: ShipmentPatch): Promise<Shipment> {
    const { id, ...rest } = patch;
    // UpdateShipment has no field mask — it merges only the fields present
    // in the body, but `pallets`/`unassignedSerials` are always replaced
    // wholesale when included, so callers must send the full desired array
    // for either one rather than a single added/changed entry.
    const raw = await this.fetchJSON(`/v1/shipments/${encodeURIComponent(id)}`, {
      method: 'POST',
      body: JSON.stringify(withoutUndefined(rest)),
    });
    return shipmentSchema.parse(raw);
  }

  async createShipment(input: ShipmentCreateInput): Promise<Shipment> {
    const pallets = input.pallets.map((pallet) => ({ ...pallet, serials: [], consumables: [] }));
    const raw = await this.fetchJSON('/v1/shipments', {
      method: 'POST',
      body: JSON.stringify(withoutUndefined({ ...input, pallets })),
    });
    return shipmentSchema.parse(raw);
  }

  async createShipmentViaCargonizer(input: ShipmentCreateViaCargonizerInput): Promise<Shipment> {
    const raw = await this.fetchJSON('/v1/shipments:createViaCargonizer', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return shipmentSchema.parse(raw);
  }

  async createReturnShipmentViaCargonizer(input: ShipmentCreateReturnInput): Promise<Shipment> {
    const raw = await this.fetchJSON('/v1/shipments:createReturnViaCargonizer', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return shipmentSchema.parse(raw);
  }

  async listReturnDestinations(): Promise<ShipmentEndpoint[]> {
    const raw = await this.fetchJSON('/v1/shipments:returnDestinations');
    const rawDestinations = Array.isArray(raw.destinations) ? raw.destinations : [];
    return rawDestinations.map((destination) => shipmentEndpointSchema.parse(destination));
  }

  async getShipmentLabel(id: string): Promise<Blob> {
    const raw = await this.fetchJSON(`/v1/shipments/${encodeURIComponent(id)}/label`);
    // bytes arrive base64-encoded in grpc-gateway JSON.
    const content = typeof raw.content === 'string' ? raw.content : '';
    const binary = atob(content);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    const contentType = typeof raw.contentType === 'string' ? raw.contentType : 'application/pdf';
    return new Blob([bytes], { type: contentType });
  }

  async transferShipmentToCarrier(id: string, version: number): Promise<Shipment> {
    const raw = await this.fetchJSON(`/v1/shipments/${encodeURIComponent(id)}:transferToCarrier`, {
      method: 'POST',
      body: JSON.stringify({ version }),
    });
    return shipmentSchema.parse(raw);
  }

  async cancelShipment(id: string, version: number, note?: string): Promise<Shipment> {
    const raw = await this.fetchJSON(`/v1/shipments/${encodeURIComponent(id)}:cancel`, {
      method: 'POST',
      body: JSON.stringify({ version, note: note?.trim() || undefined }),
    });
    return shipmentSchema.parse(raw);
  }

  async listShipmentEvidence(shipmentId: string): Promise<ShipmentEvidence[]> {
    const params = new URLSearchParams({ subjectType: 'SHIPMENT', subjectId: shipmentId });
    const raw = await this.fetchJSON(`/v1/evidence?${params.toString()}`);
    const rawEvidence = Array.isArray(raw.evidence) ? raw.evidence : [];
    return rawEvidence.map((evidence) => shipmentEvidenceSchema.parse(evidence));
  }

  async refreshShipmentTracking(shipmentIds: string[]): Promise<TrackingRefreshResult> {
    const raw = await this.fetchJSON('/v1/shipments:refreshTracking', {
      method: 'POST',
      body: JSON.stringify({ shipmentIds }),
    });
    return trackingRefreshResultSchema.parse(raw);
  }

  async listCargonizerCustomers(): Promise<CargonizerCustomer[]> {
    const raw = await this.fetchJSON('/v1/shipments:listCargonizerCustomers');
    const rawCustomers = Array.isArray(raw.customers) ? raw.customers : [];
    return rawCustomers.map((customer) => cargonizerCustomerSchema.parse(customer));
  }
}

export const shipmentsApi: ShipmentsApi = new LiveShipmentsApi();
